/**
 * Shared scalar shapes. Every timestamp is ISO 8601 UTC text (docs/53 §2.1), every day is YYYY-MM-DD,
 * every month is YYYY-MM, money is integer cents, and every list is cursor paginated.
 */
import { z } from 'zod';

/** ISO 8601 UTC timestamp with milliseconds optional, always ending in Z. */
export const IsoDateTime = z.iso.datetime({ offset: false }).meta({ examples: ['2026-10-03T14:14:00.000Z'] });
export type IsoDateTime = z.infer<typeof IsoDateTime>;

export const Day = z.iso.date().meta({ examples: ['2026-10-03'] });
export type Day = z.infer<typeof Day>;

export const Month = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected YYYY-MM')
  .meta({ examples: ['2026-10'] });
export type Month = z.infer<typeof Month>;

/** 24 hour clock time in the business's zone, e.g. 08:00. 24:00 closes an all-day interval. */
export const ClockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'expected HH:MM')
  .meta({ examples: ['08:00'] });
export type ClockTime = z.infer<typeof ClockTime>;

export const Email = z.email().max(254).meta({ examples: ['hank@dorseyheating.com'] });
export type Email = z.infer<typeof Email>;

/** E.164, US numbers only at launch (+1 then ten digits). */
export const PhoneE164 = z
  .string()
  .regex(/^\+1[2-9]\d{2}[2-9]\d{6}$/, 'expected a US number in E.164 form')
  .meta({ examples: ['+15125550142'] });
export type PhoneE164 = z.infer<typeof PhoneE164>;

/** A phone number as a person types it. Normalised to E.164 by the server. */
export const PhoneInput = z
  .string()
  .trim()
  .min(7)
  .max(32)
  .regex(/^[+()\d\s.\-]+$/, 'expected a phone number')
  .meta({ examples: ['(512) 555-0142'] });

/** IANA zone name. The server checks it against Intl before storing. */
export const TimeZone = z
  .string()
  .regex(/^[A-Za-z]+(?:[/_+-][A-Za-z0-9]+)*$/, 'expected an IANA time zone')
  .max(64)
  .meta({ examples: ['America/Chicago'] });

export const UsState = z
  .string()
  .regex(/^[A-Z]{2}$/, 'expected a USPS state code')
  .meta({ examples: ['TX'] });

export const Cents = z.number().int().min(-100_000_000).max(100_000_000);
export const PositiveCents = z.number().int().min(0).max(100_000_000);

/** Relative app path used for deep links. Never an absolute URL, so it can't redirect off site. */
export const AppPath = z
  .string()
  .regex(/^\/(?!\/)[A-Za-z0-9\-._~/%?=&]*$/, 'expected a relative app path')
  .max(512)
  .meta({ examples: ['/leads/ld_abcdefghijklmnopqrstuvwxyz'] });

export const HttpsUrl = z
  .url({ protocol: /^https$/ })
  .max(2048)
  .meta({ examples: ['https://riveraplumbing.com/services/water-heaters/'] });

export const Slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'expected a lowercase slug')
  .max(64)
  .meta({ examples: ['water-heater-repair'] });

/** A registrable domain, lowercased, e.g. riveraplumbing.com. */
export const Domain = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{4,253}$)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/, 'expected a domain like example.com')
  .meta({ examples: ['riveraplumbing.com'] });

/** Opaque pagination cursor. */
export const Cursor = z
  .string()
  .regex(/^[A-Za-z0-9_\-=.]{1,256}$/)
  .meta({ examples: ['eyJhdCI6IjIwMjYtMTAtMDMifQ'] });

export const Limit = z.coerce.number().int().min(1).max(100).default(30);

export const PageQuery = z.object({ cursor: Cursor.optional(), limit: Limit.optional() }).strict();

export function page<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: Cursor.nullable() });
}

/** Free text written by a person. Trimmed, bounded, never HTML. */
export function text(max: number, min = 1) {
  return z.string().trim().min(min).max(max);
}

/** Arbitrary JSON value, used where the contract is a fact value validated by @ff/schemas. */
export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
export const JsonValue: z.ZodType<Json> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValue), z.record(z.string(), JsonValue)]),
);

/** Every number the client sees says where it's from and when (docs/50 P5, docs/52 §5). */
export const Provenance = z.object({
  source: z.enum(['site', 'google', 'reviews', 'us', 'stripe', 'grid']),
  updatedAt: IsoDateTime.nullable(),
});

export const Ok = z.object({ ok: z.literal(true) });
